/*@flow*/
import L from 'leaflet';
import * as t from 'lib/t';
import React from 'react';
import same from 'lib/same';
import { Id } from 'types/tcomb';
import { compose, type HOC, shouldUpdate } from 'recompose';
import TextLayer from './TextLayer';

type RequiredT = {
  id: ID,
  content: string,
  latlon: Leaflet$LatLng | [number, number],
};
export type OptionsT = $Exact<
  RequiredT & {
    offset?: [number, number],
    zoomAnimation?: boolean,
  }
>;
type PropsT = { map: Leaflet$Map, options: OptionsT };
type StateT = RequiredT & {
  offset: [number, number],
  zoomAnimation: boolean,
  position: ?Leaflet$Point,
  animated: boolean,
};

const Enhancer: HOC<$Exact<PropsT>, $Exact<PropsT>> = compose(
  shouldUpdate((np, props) => !same(np.options, props.options))
);

class LabelLayer extends React.Component<PropsT, StateT> {
  state: StateT;

  constructor(props: PropsT) {
    super();

    let options = {
      offset: [0, 0],
      zoomAnimation: true,
      content: '',
      width: 200,
      ...props.options,
    };

    options = {
      ...options,
      animated: L.Browser.any3d && options.zoomAnimation,
      latlon: L.latLng(options.latlon),
      position: null,
    };

    // type check
    Id.is(options.id);
    t.union([t.Object, t.Array]).is(options.latlon);

    this.state = options;
  }

  // -------------- Lifecycle API ---------------

  componentWillMount() {
    this.updatePositionFromLatLon();
  }

  componentDidMount() {
    this.attachEventHandlers();
  }

  componentDidUpdate(prevProps) {
    if (!same(this.props.options, prevProps.options)) {
      this.setState(this.props.options); //eslint-disable-line
    }
  }

  componentWillUnmount() {
    this.detachEventHandlers();
  }

  render() {
    if (!this.position) return null;
    return <TextLayer position={this.position} content={this.state.content} />;
  }

  // -------------- Event handlers ---------------

  attachEventHandlers() {
    this.props.map.on('moveend', this.onMoveEnd, this);
    this.props.map.on('viewreset', this.onViewReset, this);

    if (this.state.animated) {
      this.props.map.on('zoomanim', this.onZoomAnimation, this);
    }
  }

  detachEventHandlers() {
    this.props.map.off(
      {
        zoomanim: this.onZoomAnimation,
        moveend: this.onMoveEnd,
        viewreset: this.onViewReset,
      },
      this
    );
  }

  onMoveEnd() {
    this.updatePositionFromLatLon();
  }

  onZoomAnimation(opt: { zoom: number, center: Leaflet$LatLng }) {
    // We use `_latLngToNewLayerPoint` because the map origin has changed
    // and we need to specify a new pixel origin using zoom and center
    // arguments. If we had used `latLngToLayerPoint`, it would have used
    // the outdated pixel origin of the map.
    let pos = this.props.map
      ._latLngToNewLayerPoint(this.state.latlon, opt.zoom, opt.center)
      .round();
    this.position = pos;
  }

  onViewReset(e?: { hard: boolean }) {
    e && e.hard && this.updatePositionFromLatLon();
  }

  // -------------- Helpers ---------------

  updatePositionFromLatLon() {
    if (!this.state.latlon) return;
    this.position = this.props.map.latLngToLayerPoint(this.state.latlon);
  }

  // -------------- Setters ---------------

  set position(value: Leaflet$Point) {
    const { offset } = this.state;
    this.setState({ position: value.add(L.point(offset)) });
  }

  // -------------- Getters ---------------
  get position() {
    return this.state.position;
  }
}

export default Enhancer(LabelLayer);
